import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import { usePrevious } from 'hooks';
import Grid from '@material-ui/core/Grid';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { diffObjects, formatAmount, parseInputNumber } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { CustomersAction } from 'state/modules/customers';

import { TextField, RadioButton, RadioButtonsGroup } from 'components/FormFields';

const formSchema = Yup.object().shape({
  is_tax_exempt: Yup.boolean(),
  spending_limit: Yup.number(),
});

const EditAccountModal = ({ open, account, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { fetchedUpdateAccount, fetchingUpdateAccount } = useSelector((state) => state.customers);
  const prevFetched = usePrevious(fetchedUpdateAccount);
  const initialValues = {
    spending_limit: formatAmount(account?.spending_limit),
    is_tax_exempt: account?.is_tax_exempt || false,
  };

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (fetchedUpdateAccount && fetchedUpdateAccount !== prevFetched && open) {
      handleClick();
      dispatch(CustomersAction.resetUpdateAccount());
    }
  }, [fetchedUpdateAccount, prevFetched, open, handleClick, dispatch]);

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      loading={fetchingUpdateAccount}
      title={i18n.t('payments.edit_account')}
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={(values) => {
          let payload = diffObjects(initialValues, values);
          if (payload.spending_limit) {
            payload.spending_limit = parseInputNumber(payload.spending_limit);
          }
          dispatch(CustomersAction.fetchUpdateAccount(account.id, payload));
        }}
        validationSchema={formSchema}
      >
        {({ handleSubmit, values, errors, touched, setFieldValue }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field
                  margin="none"
                  type="number"
                  format="price"
                  name="spending_limit"
                  component={TextField}
                  onFocus={() => {
                    if (parseFloat(values.spending_limit) === 0) {
                      setFieldValue('spending_limit', '');
                    }
                  }}
                  handleBlur={() => {
                    if (!parseFloat(values.spending_limit)) {
                      setFieldValue('spending_limit', formatAmount(0));
                    }
                  }}
                  label={i18n.t('payments.spending_limit')}
                />
              </Grid>
              <Grid item xs={12}>
                <RadioButtonsGroup
                  errors={errors}
                  touched={touched}
                  name="is_tax_exempt"
                  value={values.is_tax_exempt}
                  setFieldValue={setFieldValue}
                  label={i18n.t('payments.tax_exempt')}
                >
                  <Field value={true} component={RadioButton} label={i18n.t('common.yes')} />
                  <Field value={false} component={RadioButton} label={i18n.t('common.no')} />
                </RadioButtonsGroup>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

EditAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

EditAccountModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default EditAccountModal;
